/**
 * Swiss VAT (MWST) rates — single source of truth.
 *
 * Rates are date-dependent: the rate that applies is the one in force on the
 * date of supply, so historical transactions must keep their historical rate.
 * All rates are in basis points (810 = 8.1%).
 *
 * Source: ESTV (Eidgenössische Steuerverwaltung), MWST-Sätze.
 */

export type SwissVatRateKind = "normal" | "reduced" | "accommodation";

export interface SwissVatRateSet {
  /** First day the rates apply (Unix ms, 00:00 Europe/Zurich) */
  validFrom: number;
  normal: number;
  reduced: number;
  accommodation: number;
}

/** Midnight Europe/Zurich (CET, UTC+1) on 1 January of `year`, in Unix ms */
const zurichNewYear = (year: number) => Date.UTC(year - 1, 11, 31, 23);

/** Newest first. Dates before the oldest entry fall back to the oldest entry. */
export const SWISS_VAT_RATE_HISTORY: readonly SwissVatRateSet[] = [
  {
    validFrom: zurichNewYear(2024),
    normal: 810,
    reduced: 260,
    accommodation: 380,
  },
  {
    validFrom: zurichNewYear(2018),
    normal: 770,
    reduced: 250,
    accommodation: 370,
  },
  {
    validFrom: zurichNewYear(2011),
    normal: 800,
    reduced: 250,
    accommodation: 380,
  },
];

/** Unix ms, Date, or ISO date string (e.g. "2024-01-01") */
export type VatDateInput = number | Date | string;

/**
 * Rates in force on `date`. Defaults to now; an unparseable date also falls
 * back to now rather than to the oldest rates.
 */
export function getSwissVatRates(
  date: VatDateInput = Date.now()
): SwissVatRateSet {
  const parsed = new Date(date).getTime();
  const ts = Number.isFinite(parsed) ? parsed : Date.now();
  return (
    SWISS_VAT_RATE_HISTORY.find(set => ts >= set.validFrom) ??
    SWISS_VAT_RATE_HISTORY[SWISS_VAT_RATE_HISTORY.length - 1]
  );
}

/** Rate in basis points for a rate kind on `date`. Defaults to now. */
export function getSwissVatRate(
  kind: SwissVatRateKind,
  date?: VatDateInput
): number {
  return getSwissVatRates(date)[kind];
}

/** Rates currently in force, in basis points */
export const CURRENT_SWISS_VAT_RATES = getSwissVatRates();

/**
 * Map a rate in basis points to its kind, recognising every historical rate.
 * Returns null for 0 or any rate that was never a Swiss VAT rate.
 * 380 was the accommodation rate in both 2011–2017 and from 2024.
 */
export function classifySwissVatRate(
  basisPoints: number
): SwissVatRateKind | null {
  for (const set of SWISS_VAT_RATE_HISTORY) {
    if (basisPoints === set.normal) return "normal";
    if (basisPoints === set.reduced) return "reduced";
    if (basisPoints === set.accommodation) return "accommodation";
  }
  return null;
}

/** Format basis points as a percentage label, e.g. 810 → "8.1%" */
export function formatVatRate(basisPoints: number): string {
  return `${basisPoints / 100}%`;
}

const KIND_LABELS: Record<SwissVatRateKind, string> = {
  normal: "Standard",
  reduced: "Reduced",
  accommodation: "Accommodation",
};

/**
 * Options for VAT rate pickers, as percent strings ("8.1"): exempt, the
 * current rates, then the 2018–2023 rates so older transactions stay editable.
 */
export const SWISS_VAT_RATE_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = (() => {
  const [current, previous] = SWISS_VAT_RATE_HISTORY;
  const kinds: SwissVatRateKind[] = ["normal", "reduced", "accommodation"];
  return [
    { value: "0", label: "0% (Exempt)" },
    ...kinds.map(kind => ({
      value: String(current[kind] / 100),
      label: `${formatVatRate(current[kind])} (${KIND_LABELS[kind]})`,
    })),
    ...kinds.map(kind => ({
      value: String(previous[kind] / 100),
      label: `${formatVatRate(previous[kind])} (${KIND_LABELS[kind]}, until 2023)`,
    })),
  ];
})();
